import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Feather } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { AdminNav } from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

export default function AdminLetter() {
  useSeo({ title: "Write a letter", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("wd-admin") || "");
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadCount = async (t) => {
    try {
      const { data } = await axios.get(`${API}/letters/subscribers`, { headers: { "X-Admin-Token": t } });
      setCount(data.count);
    } catch {
      setCount(null);
    }
  };

  useEffect(() => {
    if (token) loadCount(token);
  }, []);

  const saveToken = (t) => {
    setToken(t);
    localStorage.setItem("wd-admin", t);
    setResult(null);
    if (t) loadCount(t);
  };

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/letters`,
        { subject, body, origin: window.location.origin },
        { headers: { "X-Admin-Token": token } }
      );
      setResult(`Your letter is on its way to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}${data.failed.length ? ` — ${data.failed.length} could not be delivered` : ""}.`);
      setSubject("");
      setBody("");
    } catch {
      setResult("Something went wrong — check your key and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-cream min-h-screen" data-testid="admin-letter-page">
      <section className="mx-auto max-w-2xl px-6 pt-40 pb-24">
        <div className="flex items-center gap-3 text-clay">
          <Feather size={20} strokeWidth={1.25} />
          <p className="text-xs tracking-[0.35em] uppercase">The slow letter</p>
        </div>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Write &amp; send a letter</h1>
        <p className="mt-4 text-base leading-relaxed text-soft">
          {count === null
            ? "Enter your key to see how many readers will receive it."
            : `This letter will go to ${count} subscriber${count === 1 ? "" : "s"}.`}
        </p>

        <div className="mt-8">
          <AdminNav current="/admin/letter" />
        </div>

        <form onSubmit={send} className="mt-12 space-y-8" data-testid="letter-form">
          <div>
            <label htmlFor="adm-token" className="text-xs tracking-[0.25em] uppercase text-soft">Your key</label>
            <input id="adm-token" type="password" value={token} onChange={(e) => saveToken(e.target.value)}
              placeholder="Admin key" className={inputCls} data-testid="letter-token-input" />
          </div>
          <div>
            <label htmlFor="adm-subject" className="text-xs tracking-[0.25em] uppercase text-soft">Subject</label>
            <input id="adm-subject" required value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="A slow hello, September light…" className={inputCls} data-testid="letter-subject-input" />
          </div>
          <div>
            <label htmlFor="adm-body" className="text-xs tracking-[0.25em] uppercase text-soft">The letter</label>
            <textarea id="adm-body" required rows={12} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Write as you speak. Plain text, empty lines become paragraph breaks."
              className={`${inputCls} resize-y`} data-testid="letter-body-input" />
          </div>
          <button type="submit" disabled={sending || count === null} data-testid="letter-send-button"
            className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-50">
            {sending ? "Sending…" : "Send the letter"}
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>

        {result && (
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 border border-clay/30 bg-parchment p-6 font-serif italic text-lg text-ink"
            data-testid="letter-result">
            {result}
          </motion.p>
        )}
        <p className="mt-8 text-xs leading-relaxed text-soft/70">
          Every letter ends with a note explaining why the reader received it and how to unsubscribe — handled automatically.
        </p>
      </section>
    </main>
  );
}
